import React, { useEffect } from 'react';
import { DEBOUNCE_DURATION, RoutePath, SPACING } from '../../generic/config';
import Searcher from '../../generic/Searcher';
import { Context } from '../../App';
import { debounce } from 'lodash';
import { WorksiteHit } from '../../model/worksite';
import { QueryKeys } from '../../api/base/react-query';
import { searchForWorksites } from '../../api/worksite-controller';
import { useQuery } from 'react-query';
import Table, { TableProps } from '../../base-components/Table';
import { useNavigate } from 'react-router-dom';
import EditorParent from '@/base-components/EditorParent';
import { CustomerNameCell } from '../customer/CustomerSearchPage';
import { Button } from '@chakra-ui/react';
import { Translations } from '@/generic/Translations';
import { Pager } from '../order/OrderSearchPage';
import { Paged } from '../../model/paged';
import { ActiveBadge } from './WorksitePage';

const WorksiteSearchPage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const navigate = useNavigate();

    return (
        <EditorParent title={translations.model.worksite.search.title} description={translations.model.worksite.search.description}>
            <WorksiteSearchWithResults href={(row: WorksiteHit) => `${RoutePath.Worksites}/${row.id}`}>
                <Button my={SPACING} onClick={() => navigate(`${RoutePath.Worksites}/new`)}>{translations.common.registerNew}</Button>
            </WorksiteSearchWithResults>
        </EditorParent>
    );
};

export default WorksiteSearchPage;

export const getTableProps = (translations: Translations, rows: WorksiteHit[], onRowClick?: (row: WorksiteHit) => void, href?: (row: WorksiteHit) => string, isLoading?: boolean): TableProps<WorksiteHit> => {
    return {
        columns: [
            { title: translations.model.worksite.id, scale: 1, getter: (r) => r.id },
            { title: translations.model.worksite.externalId, scale: 1, getter: (r) => r.externalId ?? "" },
            { title: translations.common.active, scale: 1, getter: (r) => <ActiveBadge active={r.active} translations={translations} /> },
            { title: translations.model.worksite.city, scale: 1, getter: (r) => r.city },
            { title: translations.model.worksite.postalCode, scale: 1, getter: (r) => r.postalCode },
            { title: translations.model.worksite.address, scale: 5, getter: (r) => r.address },
            { title: translations.model.customer.customer, scale: 3, getter: (r) => CustomerNameCell({ row: r.customerHit }) },
        ],
        rows,
        isLoading,
        href,
        onRowClick,
    };
}

export const WorksiteSearchWithResults = ({ children, onRowClick, href }: { children?: React.ReactNode; onRowClick?: (row: WorksiteHit) => void; href?: (row: WorksiteHit) => string }) => {
    const { translations } = React.useContext(Context);
    const [page, setPage] = React.useState(0);

    const doSearch = async () => {
        return await searchForWorksites(filter, page);
    };

    const worksiteHits = useQuery<Paged<WorksiteHit>>([QueryKeys.worksites, page], doSearch);

    const [filter, setFilter] = React.useState("");

    useEffect(() => {
        setPage(0);
        worksiteHits.refetch();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = debounce((value: string) => {
        setFilter(value);
    }, DEBOUNCE_DURATION);

    const onChange = (value: string) => {
        handleChange(value);
    };

    const tableProps = getTableProps(translations, worksiteHits?.data?.items ?? [], onRowClick, href, worksiteHits.isLoading);

    return (
        <>
            <Searcher {...translations.model.worksite.search} onChange={onChange}>
                {children}
            </Searcher>
            <Table tableProps={tableProps} />
            <Pager page={page} setPage={setPage}
                total={worksiteHits.data?.total ?? 0} size={worksiteHits.data?.size ?? 0}
                count={worksiteHits.data?.items.length ?? 0} />
        </>
    );
}
